import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useHeaderTabs as useHeaderTabsNext } from '@e3dc-react/shell/Libs/Header'
import SmartPowerSettings from 'src/Libs/smart-power/components/SmartPowerSettings'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import useSmartPower from 'src/Libs/smart-power/hooks/use-smart-power'
import { SmartPowerSettingsFormModel } from 'src/Libs/smart-power/models'
import { modelToSettingsForm } from 'src/Libs/smart-power/transformers'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useParams } from 'react-router-dom'
import { useFarmContext } from 'src/Elements/Farm'
import { isUndefined } from 'lodash'
import { SmartPowerValidationBoundaries } from '../types'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { makeStyles } from 'tss-react/mui'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OfflineInstallationBlankView } from 'src/Libs/offline-mode'
import BannerQueue from 'src/Libs/banners/components/BannerQueue'
import { BannerLocations } from 'src/Libs/banners/types'
import { FormikWithExitHandler } from 'src/Libs/form'
import { SmartPowerSettingsSkeleton } from 'src/Routes/SystemSettings/Settings/SystemSettingsSkeletons'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import { BlankViewImageVariants, BlankView } from 'src/Components/blank-view'
import { useSmartPowerFormInitializer } from '../hooks'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'

const MIN_CHARGE_POWER = 0
const MIN_DISCHARGE_POWER = 0

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

interface Props {
  storageId?: string
}

const SMART_POWER_TAB_INDEX = 1
const TABS_OFFSET = 2

/**
 * This function returns the smart power settings page and sets the correct active tab
 */
const SmartPowerPage: React.FC<Props> = ({ storageId }) => {
  const { setActiveTab, tabs } = useHeaderTabsNext()
  const [doProcessForm, setDoProcessForm] = useState<boolean>(false)
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const dispatch = useDispatch()
  const pageName = t('systemSettings.smartFunctions.smartPower.pageTitle')
  const accordionName = t('systemSettings.smartFunctions.smartPower.settings.sectionTitle')
  const { isStorageOnline, offlineClientStorageIds } = useContext(InstallationOnlineStatusContext)
  const { childStorageId } = useParams<{ childStorageId: string }>()
  const { isFarm, isFarmMasterSelected, farmClients } = useFarmContext()
  const { classes } = useStyles()
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()
  const { triggerAppRating } = useAppRatingContext()

  /**
   *  This function returns the index of the tabs in context of Farm or Single Storage Installation.
   */
  const getTabIndex = (): number => {
    if (!farmClients || (isFarm && isUndefined(childStorageId))) return SMART_POWER_TAB_INDEX
    const selectedTabClientIndex = farmClients.findIndex(client => client.storageId === childStorageId)
    return selectedTabClientIndex + TABS_OFFSET
  }

  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const currentStorageId = childStorageId ?? storageId
  const {
    isLoading: isSmartPowerDataLoading,
    isError: isSmartPowerDataError,
    smartPowerSettings,
    loadSettings,
    updateSettings,
  } = useSmartPower(currentStorageId)

  const [formBoundaries, setFormBoundaries] = useState<SmartPowerValidationBoundaries>({
    minStartDischargePower: smartPowerSettings?.emsSysSpec.minStartDischargePower ?? 0,
    minChargePower: MIN_CHARGE_POWER,
    minDischargePower: MIN_DISCHARGE_POWER,
    maxChargePower: smartPowerSettings?.emsSysSpec.maxChargePower ?? 0,
    maxDischargePower: smartPowerSettings?.emsSysSpec.maxDischargePower ?? 0,
    maxStartDischargePower: smartPowerSettings?.emsSysSpec.maxStartDischargePower ?? 0,
  })

  const { initialValues, validationSchema } = useSmartPowerFormInitializer(smartPowerSettings, formBoundaries)
  const [formValues, setFormValues] = useState<SmartPowerSettingsFormModel>(initialValues)

  useEffect(() => {
    setActiveTab(getTabIndex())
  }, [tabs, farmClients, childStorageId]) // eslint-disable-line

  useEffect(() => {
    dispatch(appUpdate({ title: pageName }))
  }, [currentStorageId]) // eslint-disable-line

  useEffect(() => {
    if (smartPowerSettings) {
      setFormValues(modelToSettingsForm(smartPowerSettings))
      setFormBoundaries({
        minStartDischargePower: smartPowerSettings.emsSysSpec.minStartDischargePower,
        minChargePower: MIN_CHARGE_POWER,
        minDischargePower: MIN_DISCHARGE_POWER,
        maxChargePower: smartPowerSettings.emsSysSpec.maxChargePower,
        maxDischargePower: smartPowerSettings.emsSysSpec.maxDischargePower,
        maxStartDischargePower: smartPowerSettings.emsSysSpec.maxStartDischargePower,
      })
    }
  }, [smartPowerSettings])

  const handleSubmit = useCallback(
    async (data: SmartPowerSettingsFormModel) => {
      setDoProcessForm(true)
      try {
        await updateSettings(data)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName, isEmsSetting: true })
        triggerAppRating()
      } catch (error) {
        /* revert changes */
        if (formValues) setFormValues({ ...formValues })
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))
      } finally {
        setDoProcessForm(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, loadSettings, t, updateSettings, formValues, handleDisplaySnackbarMessageBasedOnSoftwareVersion, triggerAppRating]
  )

  const isDataLoading = isSmartPowerDataLoading
  const isNoDataAvailable = !isSmartPowerDataLoading && !formValues
  const isOffline = !isStorageOnline || (isFarmMasterSelected && offlineClientStorageIds?.includes(childStorageId ?? ''))

  if (isFlowTheme) return null

  if (isOffline) return <OfflineInstallationBlankView />

  if (isDataLoading) return <SmartPowerSettingsSkeleton />

  if (isNoDataAvailable) {
    return (
      <BlankView
        title={'systemSettings.api.no-data-available.title'}
        explanation={'systemSettings.api.no-data-available.explanation'}
        imageVariant={isSmartPowerDataError ? BlankViewImageVariants.Worried : BlankViewImageVariants.Neutral}
      />
    )
  }
  return (
    <>
      <BannerQueue location={BannerLocations.SettingsSmartPower} sx={{ mb: 2 }} />
      <AccordionItem
        title={t('systemSettings.smartFunctions.smartPower.settings.sectionTitle')}
        contentClassName={classes.contentAccordion}
        headerContentClassName={classes.headerContent}
        expanded
      >
        <Box>
          {formValues && (
            <FormikWithExitHandler
              formKey="smart-power-data-form"
              initialValues={formValues}
              onSubmit={handleSubmit}
              enableReinitialize
              validationSchema={validationSchema}
            >
              <SmartPowerSettings
                model={formValues}
                isLoading={doProcessForm}
                isReadOnlyAccess={!hasInstallationWritingRights}
                formBoundaries={formBoundaries}
              />
            </FormikWithExitHandler>
          )}
        </Box>
      </AccordionItem>
    </>
  )
}

export default SmartPowerPage
